import type { LocationQuery } from '#vue-router'
import type { RpcContext } from '@scayle/storefront-nuxt'
import { traverse } from 'async-traverse-tree'
import type { ISbResult } from 'storyblok-js-client'
import type {
  Sbasset,
  SbContentPage,
  SbLinkObject,
  SbListingPage,
  SbMultilinkObject,
  SbNdlCity,
  SbPage,
  SbRichTextLink,
  SbStore,
  SbStorePage,
  SbStoresGlobal,
  SbUtilityPage,
} from '../storyblok/types/storyblok'
import { getNormalizedLocale } from './locale'
import { trailingSlash } from './route'
import {
  isFimObject,
  isSbHero,
  isSbListingPage,
  isSbSlide,
  isSbSlideShow,
  isSbTeaser,
} from './typeguards'
import type { ObjectWith } from './types'
import type { SbBlokData } from '@storyblok/vue'
import type { $Img } from '@nuxt/image'

const join = (arr: Array<string | undefined>) =>
  arr
    .map((folder) => {
      if (typeof folder === 'string') {
        return folder.replace(/(^\/+)|(\/+$)/g, '')
      }
      return undefined
    })
    .filter(Boolean)
    .join('/')
/**
 * get normalized country based storyblok base folder without trailing or leading slashes
 * @param $currentShop
 * @param baseFolder
 * @returns
 */
export const getLocalizedBaseFolder = (
  $currentShop: ObjectWith<{ cmsBaseFolder: string }>,
  baseFolder?: string,
) => {
  return join([$currentShop?.cmsBaseFolder, baseFolder])
}

export const getLocalizedStoryPath = (
  $currentShop: ObjectWith<{ cmsBaseFolder: string }>,
  slug: string,
  baseFolder?: string,
) => {
  return join([getLocalizedBaseFolder($currentShop, baseFolder), slug])
}

export const getRedirectToFromTranslatedSlug = (
  slug:
    | {
        lang: string
        path: string
      }
    | undefined,
) => {
  if (!slug?.lang || !slug?.path) {
    return
  }
  const parts = slug.path.split('/')
  if (Array.isArray(parts)) {
    return parts.slice(2).join('/')
  }
}

export const normalizeFullSlug = (fullSlug: string) => {
  return fullSlug.replace(/^.*\/content\//, '/').replace(/([^/])$/, '$1/')
}

const hasFullSlug = (value: any): value is ObjectWith<{ full_slug: string }> =>
  isFimObject(value) && typeof value.full_slug === 'string'

// Reserved component names in Vue are e.g.: Image, Video, Text
const isReservedComponentName = (key: string, value: any): value is string =>
  key === 'component' &&
  typeof value === 'string' &&
  ['Button', 'Image', 'Video', 'Text', 'Link'].includes(value)

const isSbRichTextLink = (value: any): value is SbRichTextLink =>
  value?.type === 'link' && value?.attrs?.uuid

const isSbMultilink = (value: any): value is SbMultilinkObject => {
  return (
    typeof value === 'object' &&
    value !== null &&
    value.fieldtype === 'multilink'
  )
}

const isAsset = (value: any): value is Sbasset =>
  typeof value === 'object' && value !== null && value.fieldtype === 'asset'

type ResolvedAssets = Omit<Sbasset, 'meta_data'> & {
  content_type: string
  content_length: number
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  alt: string
  title: string
  copyright: string
  focus: string
  is_private: boolean
  s3_filename: string
  meta_data: Record<string, string>
}

export const processStoryblokResult = async (
  input: ISbResult['data'],
  context: RpcContext,
): Promise<ISbResult['data']> => {
  const links: SbLinkObject[] | undefined = input?.links
  const assets: ResolvedAssets[] | undefined = input?.assets
  // use country specific language if available
  const language = getNormalizedLocale(context.locale).toLowerCase()
  const localeSuffix = `__i18n__${getNormalizedLocale(context.locale, '_').toLowerCase()}`

  const getAssetTranslation = (
    asset: ResolvedAssets,
    key: 'alt' | 'title' | 'copyright' | 'focus',
    defaultValue?: string | null,
  ) =>
    (asset?.meta_data?.[`${key}${localeSuffix}`] ??
      asset?.meta_data?.[key] ??
      asset?.[key]) ||
    defaultValue

  return await traverse<ISbResult['data'], ISbResult['data']>(
    input,
    async (key, value) => {
      // resolve assets
      if (isAsset(value)) {
        const asset = assets?.find((asset) => asset.id === value.id)

        if (!asset) {
          return value
        }

        return {
          ...value,
          alt: getAssetTranslation(asset, 'alt', value.alt),
          copyright: getAssetTranslation(asset, 'copyright', value.copyright),
          title: getAssetTranslation(asset, 'title', value.title),
          focus: getAssetTranslation(asset, 'focus', value.focus),
        }
      }

      // resolve links generated by the translatable slugs plugin
      if (isSbMultilink(value)) {
        const link = links?.find((link) => link.uuid === value.id)

        if (
          (value.linktype === 'story' && (!link || !value.id)) ||
          (value.linktype === 'url' && !value.url)
        ) {
          return undefined
        }

        const alternate = link?.alternates?.find(
          (link) => link.lang === language,
        )
        if (alternate) {
          const url = await resolveUrl(
            normalizeFullSlug(alternate.path),
            context,
          )
          return {
            ...value,
            original_cached_url: value.cached_url,
            cached_url: trailingSlash(url),
            alternates: link?.alternates ?? [],
          }
        }

        // For URL multilinks, fall back to url property if cached_url is not available
        const urlToResolve =
          value.cached_url || (value.linktype === 'url' ? value.url : undefined)

        if (!urlToResolve) {
          return value
        }

        const url = await resolveUrl(urlToResolve, context)
        return {
          ...value,
          original_cached_url: value.cached_url,
          cached_url: trailingSlash(url),
        }
      }

      if (isSbRichTextLink(value)) {
        const link = links?.find((link) => link.uuid === value.attrs.uuid)
        const alternate = link?.alternates?.find(
          (link) => link.lang === language,
        )
        if (alternate) {
          const url = await resolveUrl(
            normalizeFullSlug(alternate.path),
            context,
          )
          return {
            ...value,
            attrs: {
              ...value.attrs,
              href: trailingSlash(url),
              original_href: value.attrs.href,
            },
          }
        }

        const url = await resolveUrl(value.attrs.href, context)
        return {
          ...value,
          attrs: {
            ...value.attrs,
            href: trailingSlash(url),
            original_href: value.attrs.href,
          },
        }
      }

      if (key === 'cta_url' && typeof value === 'string') {
        const url = await resolveUrl(value, context)
        return trailingSlash(url)
      }

      // Image is a reserved component name in Vue, so we use the CmsImage component name
      if (typeof key === 'string' && isReservedComponentName(key, value)) {
        return `Cms${value}`
      }

      // normalize full_slug
      if (hasFullSlug(value)) {
        return {
          ...value,
          full_slug: trailingSlash(normalizeFullSlug(value.full_slug)),
        }
      }

      return value
    },
  )
}

const resolveUrl = async (url: string, context: RpcContext) => {
  const idMatch = url.match(/\/(categories|products)\/(\d+)/)
  if (idMatch) {
    const [, type, _id] = idMatch
    const id = parseInt(_id, 10)

    switch (type) {
      case 'categories':
        try {
          const category = await context.bapiClient.categories.getById(id)
          return category.path
        } catch (e) {
          return `/${id}/`
        }
      case 'products':
        return `/p/${id}/`
    }
  }

  const pathMatch = url.match(/\/(product-categories)\/(.*)/)
  if (pathMatch) {
    const [, type, path] = pathMatch

    try {
      // eslint-disable-next-line sonarjs/no-small-switch
      switch (type) {
        case 'product-categories': {
          const category = await context.bapiClient.categories.getByPath([
            path.replace(/\/$/, ''),
          ])
          return category.path
        }
      }
    } catch (e) {
      return url.replace(/.*\/product-categories\//, '/')
    }
  }

  if (
    url.startsWith('/') ||
    url.startsWith('#') ||
    url.startsWith('?') ||
    url.includes('://')
  ) {
    return url
  }

  return '/' + url
}

export const getIsInEditorMode = (query: LocationQuery) => '_storyblok' in query

const findPageStageImage = (
  entry?: MaybeRefOrGetter<SbBlokData | undefined>,
): string | undefined => {
  const value = toValue(entry)
  if (!value) {
    return undefined
  }

  if (isSbPage(value) || isSbListingPage(value)) {
    return findPageStageImage(value.stage?.[0])
  }

  if (isSbImage(value)) {
    return value.desktop_image?.filename ?? value.mobile_image?.filename
  }

  if (isSbTeaser(value)) {
    return findPageStageImage(getImageRef(value.image_ref))
  }

  if (isSbSlideShow(value)) {
    return findPageStageImage(value.slides?.[0])
  }

  if (isSbSlide(value)) {
    return findPageStageImage(getImageRef(value.image))
  }

  // Uncomment this if you want to support Hero in the future
  // if (isSbHero(value)) {
  //   return findPageStageImage(getImageRef(value.image_ref))
  // }

  return undefined
}

export const getCmsMeta = (
  page: MaybeRefOrGetter<
    | SbPage
    | SbListingPage
    | SbUtilityPage
    | SbStorePage
    | SbStoresGlobal
    | SbStore
    | SbNdlCity
    | SbContentPage
    | undefined
  >,
  $img: $Img,
) => {
  const robots = computed(() => {
    const p = toValue(page)
    return typeof p?.robots === 'string' ? p?.robots : 'index, follow'
  })
  const seo = computed(() => toValue(page)?.SEO)

  const ogImage = computed(() => {
    const src = seo.value?.og_image || findPageStageImage(page)

    // Recommended size for og:image is 1200:630
    // https://developers.facebook.com/docs/sharing/webmasters/images/
    return src
      ? $img(src, {
          width: 1200,
          height: 630,
        })
      : undefined
  })
  const twitterImage = computed(() => {
    const src = seo.value?.twitter_image || ogImage.value
    return src ? $img(src) : undefined
  })

  return {
    title: () => seo.value?.title,
    description: () => seo.value?.description,
    robots: () => robots.value || 'index, follow',
    twitterCard: () => (ogImage.value ? 'summary_large_image' : 'summary'),
    twitterDescription: () =>
      seo.value?.twitter_description
        ? seo.value?.twitter_description
        : seo.value?.description,
    twitterTitle: () =>
      seo.value?.twitter_title ? seo.value?.twitter_title : seo.value?.title,
    ...(twitterImage.value ? { twitterImage: () => twitterImage.value } : {}),
    ogTitle: () =>
      seo.value?.og_title ? seo.value?.og_title : seo.value?.title,
    ogDescription: () =>
      seo.value?.og_description
        ? seo.value?.og_description
        : seo.value?.description,
    ...(ogImage.value ? { ogImage: () => ogImage.value } : {}),
  }
}

export type CmsMeta = Partial<ReturnType<typeof getCmsMeta>>
